import React, { useEffect, useState } from 'react'
import { EnumType } from 'json-to-graphql-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import classNames from 'classnames'
import Preview from '../../../../containers/CustomDashboardv2/Dashboard/details/editor/Preview'
import { getDashboard, metricDevices } from '../../../../api/dashboard'
import { defaultNotifier } from 'functions/notificationHandler'
import { handleMutation, handleApiResponse } from '../../../../utils/request'
import { mapChartDataToPreviewProps } from './contentUtils'
import { FilledCheckIcon } from '../../../../images/common'
import { INSERT_CHART_TYPES } from '../../constants'
import './AskVisoInsertCharts.scss'

import {transformBucketedTimeToTime} from '../../../../containers/CustomDashboardv2/Dashboard/details/editor/utils'

const AskVisoInsertCharts = ({
  chart, color, dashboardDetails, devicesData = [], toggleReloadDataOnClose, item, layout
}) => {
  const [isLoading, toggleLoading] = useState(false)
  const [done, setDone] = useState(false)
  const [previewDevices, setPreviewDevices] = useState([])
  const [previewProps, setPreviewProps] = useState(null)
  const isExistingChart = item.type === 'reviewVisoChart' || item.type === 'improveVisoChart'
  const typeConfig = () => {
    if (item.removeChart) {
      return INSERT_CHART_TYPES.removeVisoChart
    } else {
      return INSERT_CHART_TYPES[item.type]
    }
  }

  const getMetricDevices = async () => {
    if (chart.tableName) {
      const onSuccess = (response) => {
        const buildDevices = response.length > 0 ? response.map(device => device.name) : []
        setPreviewDevices(buildDevices)
        const getPreviewProps = mapChartDataToPreviewProps({ chart, dashboardDetails: dashboardDetails?.details, color, devicesData, previewDevices: buildDevices })
        setPreviewProps(getPreviewProps)
      }

      try {
        const metricDevicesData = await metricDevices({
          id: dashboardDetails?.details._id,
          tableName: chart.tableName
        })
        handleApiResponse(metricDevicesData, onSuccess, 'metricDevices')
      } catch (error) {
        defaultNotifier(error)
      }
    }
  }

  useEffect(() => { getMetricDevices() }, [])

  const onUpdateLayout = async () => {

    const onSuccess = (response) => {
      let findChartId = undefined
      let dashboardLayout = []
      let updateLayout = []
      if (isExistingChart) {
        findChartId = layout.i
        dashboardLayout = dashboardDetails?.details?.configuration?.layout || []
        dashboardLayout.forEach(l => {
          const chartLayout = l.i === findChartId ? layout : l
          updateLayout.push(chartLayout)
        })
      } else {
        findChartId = response.charts.find(c => c.title === chart.title)
        dashboardLayout = response?.configuration?.layout || []
        updateLayout = [...dashboardLayout, { ...layout, i: findChartId?.id }]
      }
      if (findChartId && layout) {
        dashboardDetails?.updateLayout && dashboardDetails.updateLayout(updateLayout)
      }
      toggleReloadDataOnClose && toggleReloadDataOnClose(true)
      setDone(true)
    }

    try {
      const response = await getDashboard({ id: dashboardDetails?.details._id })
      handleApiResponse(response, onSuccess, 'dashboard')
    } catch (error) {
      defaultNotifier(error)
    } finally {
      toggleLoading(false)
    }
  }

  const onSubmit = async (chart) => {
    if (!done && typeConfig()) {
      toggleLoading(true)
      const mutationName = typeConfig()?.mutation
      const mutationId = typeConfig()?.id
      const chartTime = chart.timeRangeConfig
      let setTimeRange = { useDashboardDefault: true }
      let setDevices = { useDashboardDefault: true }

      if (!chartTime.useDashboardDefault) {
        if (chartTime.mode === 'relative') {
          setTimeRange = {
            useDashboardDefault: false,
            mode: new EnumType('relative'),
            relative: {
              unit: new EnumType(chartTime.relative.unit.toLowerCase()),
              value: Number(chartTime.relative.value)
            }
          }
        } else {
          setTimeRange = {
            useDashboardDefault: false,
            mode: new EnumType('absolute'),
            absolute: {
              start: chartTime.start,
              end: chartTime.end
            }
          }
        }
      }

      if (!chart.deviceConfig.useDashboardDefault) {
        setDevices = {
          useDashboardDefault: false,
          value: previewDevices
        }
      }

      let payload = {
        ...chart,
        deviceConfig: setDevices,
        timeRangeConfig: setTimeRange,
        type: new EnumType(chart.type),
        configuration: JSON.stringify(transformBucketedTimeToTime(chart.configuration))
      }

      if (mutationId === 'createChart') {
        const { id, ...rest } = payload
        payload = rest
      }

      if (mutationId === 'updateChart') {
        const { dashboardId, ...rest } = payload
        payload = rest
      }

      if (mutationId === 'deleteChart') {
        payload = { id: chart.id }
      }
      try {
        const response = await mutationName(payload)
        handleMutation(response, onUpdateLayout)
      } catch (error) {
        defaultNotifier(error)
      }
    }
  }

  return (
    <div className='ask-viso-insert-charts'>
      {previewProps && <Preview blankPreview {...previewProps} />}
      {item.fix &&
        <div className='ask-viso-markdown__fix'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`**Fix:** ${item.fix}`}</ReactMarkdown>
        </div>
      }
      {item.reason &&
        <div className='ask-viso-markdown__reason'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`**Reason:** ${item.reason}`}</ReactMarkdown>
        </div>

      }
      {item.impact &&
        <div className='ask-viso-markdown__impact'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`**Impact:** ${item.impact}`}</ReactMarkdown>
        </div>
      }
      {item.purpose &&
        <div className='ask-viso-markdown__purpose'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`**Purpose:** ${item.purpose}`}</ReactMarkdown>
        </div>
      }
      {item.placement &&
        <div className='ask-viso-markdown__placement'>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`**Placement:** ${item.placement}`}</ReactMarkdown>
        </div>
      }
      <div className='ask-viso-insert-charts__insert'>
        <button
          className={classNames('ask-viso-insert-charts__button', done && 'ask-viso-insert-charts__button--success')}
          onClick={() => onSubmit(chart)}
          disabled={isLoading}
        >
          {done && <FilledCheckIcon />}
          {done ? typeConfig().buttonDone : typeConfig().buttonLabel}
        </button>
      </div>
    </div>
  )
}

export default AskVisoInsertCharts